import { ROW_KIND_BUDGET } from "./constants";
import type {
  ExistingCategory,
  NewCategoryCandidate,
  ParsedRow,
  ValidationIssue,
  ValidationResult,
} from "./types";

// 重複判定: (年度, 収入/支出, カテゴリ, 明細項目, 予算/実績)。複数月は1行にまとめる前提。
const dupKey = (r: ParsedRow) =>
  `${r.fiscalYear}__${r.group}__${r.categoryGroup}__${r.category}__${r.rowKind}`;

/**
 * パース済み行の意味検証 (CSV v3.2 準拠)。
 * - 必須: 年度 / カテゴリ / 明細項目 / (収入・支出/予算実績は parser で検証済)
 * - 明細項目: 収入は登録済のみ。支出はCSV経由で新規登録可。
 * - カテゴリ: 明細項目の所属カテゴリ(revenue_group/expense_group)と一致すること
 * - 金額: 収入・支出とも複数月可
 * - CSV 内の重複検知 (実績)
 */
export function validateBudgetCsv(
  rows: ParsedRow[],
  context: {
    existingCategories: ExistingCategory[];
  },
): ValidationResult {
  const errors: ValidationIssue[] = [];
  const warnings: ValidationIssue[] = [];
  const notices: ValidationIssue[] = [];
  const newCategories = new Map<string, NewCategoryCandidate>();

  // 同じ name が異なる group で複数存在しうる (収入の「その他」/支出の「その他」)。
  const existingByGroupName = new Map<string, ExistingCategory>();
  const existingByName = new Map<string, ExistingCategory>();
  for (const c of context.existingCategories) {
    existingByGroupName.set(`${c.group}__${c.name}`, c);
    existingByName.set(c.name, c);
  }

  const seenDupKeys = new Map<string, number>();

  for (const row of rows) {
    // 年度
    if (row.fiscalYear === "") {
      errors.push({
        severity: "error",
        lineNumber: row.lineNumber,
        field: "年度",
        message: '"年度" は必須です',
      });
    } else if (!/^\d{4}$/.test(row.fiscalYear)) {
      errors.push({
        severity: "error",
        lineNumber: row.lineNumber,
        field: "年度",
        message: `"年度" は 4 桁数字 (実際: "${row.fiscalYear}")`,
      });
    }

    // 明細項目 (必須)
    if (row.category === "") {
      errors.push({
        severity: "error",
        lineNumber: row.lineNumber,
        field: "明細項目",
        message: '"明細項目" は必須です',
      });
    }

    // カテゴリ (必須)
    if (row.categoryGroup === "") {
      errors.push({
        severity: "error",
        lineNumber: row.lineNumber,
        field: "カテゴリ",
        message: '"カテゴリ" は必須です',
      });
    }

    // 明細項目マスタ整合 (登録済 + 収入/支出一致 + カテゴリ一致)
    if (row.category !== "") {
      const exactMatch = existingByGroupName.get(
        `${row.group}__${row.category}`,
      );
      if (exactMatch) {
        // 収入/支出 と name は一致。カテゴリ(group)も一致するか検証。
        if (
          exactMatch.categoryGroup !== undefined &&
          row.categoryGroup !== "" &&
          exactMatch.categoryGroup !== row.categoryGroup
        ) {
          errors.push({
            severity: "error",
            lineNumber: row.lineNumber,
            field: "カテゴリ",
            message: `明細項目 "${row.category}" のカテゴリは "${exactMatch.categoryGroup}" ですが CSV では "${row.categoryGroup}"`,
          });
        }
      } else {
        const nameOnlyMatch = existingByName.get(row.category);
        if (nameOnlyMatch) {
          errors.push({
            severity: "error",
            lineNumber: row.lineNumber,
            field: "収入/支出",
            message: `明細項目 "${row.category}" の収入/支出は "${nameOnlyMatch.group}" ですが CSV では "${row.group}"`,
          });
        } else {
          if (row.group === "支出") {
            const key = `${row.group}__${row.categoryGroup}__${row.category}`;
            if (!newCategories.has(key)) {
              newCategories.set(key, {
                name: row.category,
                group: row.group,
                categoryGroup: row.categoryGroup,
                firstSeenLine: row.lineNumber,
              });
            }
          } else {
            errors.push({
              severity: "error",
              lineNumber: row.lineNumber,
              field: "明細項目",
              message: `明細項目「${row.category}」は使用できません`,
            });
          }
        }
      }
    }

    // 重複検知 (予算・実績の両方)。複数月はまとめる前提なので月は鍵に含めない。
    // 予算行が重複したまま取込まれると BE で上書きされ最後の行の値しか残らないため、
    // 実績だけでなく予算行も 1 行にまとめさせる。
    const dk = dupKey(row);
    if (seenDupKeys.has(dk)) {
      errors.push({
        severity: "error",
        lineNumber: row.lineNumber,
        message: `同年度・同カテゴリ・同明細項目 "${row.category}" の${row.rowKind}行が ${seenDupKeys.get(dk)} 行目と重複しています。1 行にまとめてください。`,
      });
    } else {
      seenDupKeys.set(dk, row.lineNumber);
    }
  }

  // ── 集計レベルチェック: (年度, 明細項目) 単位で実績合計 vs 予算合計 ──
  const aggregates = new Map<
    string,
    { fiscalYear: string; category: string; budget: number; actual: number }
  >();
  for (const row of rows) {
    if (row.fiscalYear === "" || row.category === "") continue;
    const key = `${row.fiscalYear}__${row.category}`;
    let agg = aggregates.get(key);
    if (!agg) {
      agg = {
        fiscalYear: row.fiscalYear,
        category: row.category,
        budget: 0,
        actual: 0,
      };
      aggregates.set(key, agg);
    }
    const sum = Object.values(row.months).reduce<number>(
      (s, v) => s + (v ?? 0),
      0,
    );
    if (row.rowKind === ROW_KIND_BUDGET) agg.budget += sum;
    else agg.actual += sum;
  }
  for (const agg of aggregates.values()) {
    if (agg.budget <= 0) continue;
    const ratio = agg.actual / agg.budget;
    if (ratio >= 2) {
      warnings.push({
        severity: "warning",
        lineNumber: 0,
        message: `明細項目 "${agg.category}" (${agg.fiscalYear}年度) の実績合計 ${agg.actual.toLocaleString()}円が予算 ${agg.budget.toLocaleString()}円の ${Math.round(ratio * 100)}% に達しています`,
      });
    } else if (agg.actual > agg.budget) {
      notices.push({
        severity: "notice",
        lineNumber: 0,
        message: `明細項目 "${agg.category}" (${agg.fiscalYear}年度) の実績合計 ${agg.actual.toLocaleString()}円が予算 ${agg.budget.toLocaleString()}円を超過しています`,
      });
    }
  }

  return {
    errors,
    warnings,
    notices,
    newCategories: Array.from(newCategories.values()),
  };
}
